import {
  CloseOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  SelectInput,
  TextInput,
  AlertBox,
  Button,
} from "@life-line-lab/components-react";
import { Row, Col, Input } from "antd";
import {
  GoodsHasTrackingNo,
  HasMultipleGoods,
  ParcelTypeEnums,
} from "Data/order";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import TextArea from "antd/lib/input/TextArea";

interface SettingsProps {
  parcelInput: any;
  error: any;
  parentIndex: number;
  input: any
}

type indexType = {
  parentIndex: number;
  childIndex?: number;
};

interface BaseSettingsProps extends SettingsProps {
  handleSelectChange: (value: string | string[], name: string) => void;
  handleArrTextChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: indexType,
  ) => void;
}

const BaseInformation: FC<BaseSettingsProps> = (props) => {
  const { t } = useTranslation();
  const { parcelInput, error, handleArrTextChange, parentIndex } = props || {};
  
  return (
    <div className="ocf-pi-base">
      <Row gutter={[16, 16]}>
        <Col span={24} lg={12}>
          <div className="mb-15">
            <TextInput
              label={t("label.weight")}
              placeholder={t("placeholder.weight")}
              name="weight"
              value={parcelInput?.weight}
              isRequired={true}
              onChange={(e) => handleArrTextChange(e, { parentIndex })}
            />
            <AlertBox text={error.weight} />
          </div>
          <div className="mb-15">
            <label className="inp-label">{t("label.remarks")}</label>
            <TextArea
              id="remarks"
              size="middle"
              autoSize={{ minRows: 2, maxRows: 6 }}
              name="remarks"
              value={parcelInput?.remarks}
              onChange={(e) => handleArrTextChange(e, { parentIndex })}
              placeholder={t("txt.remarks")}
            />
            <AlertBox text={error.remarks} />
          </div>
        </Col>
        <Col span={24} lg={12}>
          <label className="inp-label inp-label--required">
            {t("label.maxDimensions")}
          </label>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.length")}
              value={parcelInput?.length}
              name="length"
              onChange={(e) => handleArrTextChange(e, { parentIndex })}
            />
            <AlertBox text={error["maxDimensions.length"]} />
          </div>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.width")}
              value={parcelInput?.width}
              name="width"
              onChange={(e) => handleArrTextChange(e, { parentIndex })}
            />
            <AlertBox text={error["maxDimensions.width"]} />
          </div>
          <div className="mb-15">
            <TextInput
              isRequired={true}
              placeholder={t("txt.height")}
              value={parcelInput?.height}
              name="height"
              onChange={(e) => handleArrTextChange(e, { parentIndex })}
            />
            <AlertBox text={error["maxDimensions.height"]} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const GoodsProperties: FC<any> = (props) => {
  const { t } = useTranslation();

  const {
    status,
    input,
    error,
    goodCareConditionList,
    handleArrSelectChange,
    handleArrTextChange,
    goodsLength = 1,
    parcelInput,
    handleAddGoods,
    handleDeleteGoods,
    parentIndex,
  } = props || {};

  const { TYPE_OF_GOODS = [] } = goodCareConditionList || {};

  const enableMultipleGoods = HasMultipleGoods.includes(input?.parcelType);
  const enableGoodsTrackingNo = GoodsHasTrackingNo.includes(input?.parcelType);

  const dynamicColunmSize = useMemo(() => {
    if (enableMultipleGoods) return 7;
    if (enableGoodsTrackingNo) return 8;

    return 12;
  }, [enableGoodsTrackingNo, enableMultipleGoods]);

  return (
    <div className="ocf-pi-goods">
      <div className="ocf-pig-container">
        <div className="ocf-pig-label">
          <Row gutter={[16, 16]}>
            {enableMultipleGoods && (
              <Col span={24} lg={1}>
                {/* <label className="inp-label">{t("label.goods")}</label> */}
              </Col>
            )}
            <Col span={24} lg={dynamicColunmSize}>
              <label className="inp-label inp-label--required">
                {t("label.typeOfGood")}
              </label>
            </Col>
            <Col span={24} lg={dynamicColunmSize}>
              <label className="inp-label inp-label--required">
                {t("label.goodsValue")}
              </label>
            </Col>
            {enableGoodsTrackingNo && (
              <Col span={24} lg={7}>
                <label className="inp-label inp-label">
                  {t("label.trackingNo")}
                </label>
              </Col>
            )}
          </Row>
        </div>
        <div className="ocf-pig-parcels">
          {parcelInput.goods.map((g, i) => {
            return (
              <Row gutter={[16, 16]} align="middle" key={i}>
                {enableMultipleGoods && (
                  <Col span={24} lg={1}>
                    {i + 1}
                  </Col>
                )}
                <Col span={24} lg={dynamicColunmSize}>
                  <SelectInput
                    showSearch
                    options={TYPE_OF_GOODS.map((t) => {
                      const label = t?.isProhibited ? `${t?.name} ⚠️` : t?.name;

                      return {
                        label,
                        value: t?.id,
                      };
                    })}
                    filterOption={(inputValue, option) =>
                      option.label
                        .toLowerCase()
                        .indexOf(inputValue.toLowerCase()) >= 0
                    }
                    name="typeOfGood"
                    value={g?.typeOfGood}
                    placeholder={t("placeholder.typeOfGood")}
                    isRequired={true}
                    onChange={(value) => {
                      handleArrSelectChange("typeOfGood", value, {
                        parentIndex,
                        childIndex: i,
                      });
                    }}
                  />
                  <AlertBox
                    text={
                      error[`parcels[${parentIndex}].goods[${i}].typeOfGood`]
                    }
                  />
                </Col>
                <Col span={24} lg={dynamicColunmSize}>
                  <TextInput
                    name="goodsValue"
                    isRequired={true}
                    placeholder={t("txt.rm")}
                    value={g?.goodsValue}
                    onChange={(e) =>
                      handleArrTextChange(e, { parentIndex, childIndex: i })
                    }
                  />
                  <AlertBox
                    text={
                      error[`parcels[${parentIndex}].goods[${i}].goodsValue`]
                    }
                  />
                </Col>
                {enableGoodsTrackingNo && (
                  <Col span={24} lg={dynamicColunmSize}>
                    <TextInput
                      name="trackingNumber"
                      placeholder={t("label.enterHere")}
                      value={g.trackingNumber}
                      onChange={(e) =>
                        handleArrTextChange(e, { parentIndex, childIndex: i })
                      }
                    />
                    <AlertBox
                      text={
                        error[
                          `parcels[${parentIndex}].goods[${i}].trackingNumber`
                        ]
                      }
                    />
                  </Col>
                )}
                {enableMultipleGoods && goodsLength > 1 && (
                  <Col span={2}>
                    <div className="pib-parcel-btn-container">
                      <div
                        className={classnames("pib-delete-btn", {
                          disabled: goodsLength > 1,
                        })}
                      >
                        <CloseOutlined
                          onClick={() =>
                            goodsLength > 1 && handleDeleteGoods(parentIndex, i)
                          }
                        />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            );
          })}
        </div>
      </div>
      {enableMultipleGoods && (
        <div className="ocf-pig-footer">
          <Button
            isLoading={status === "loading"}
            label={t("action.addGoods")}
            onClick={() => handleAddGoods(parentIndex)}
            style={{ width: "100%" }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Order processing must support split parcel, which require each set of parcel information
 * @param props
 * @returns
 */
const ParcelInfoBundle: FC<any> = (props) => {
  const { parentIndex = 0, parcelLength = 1, onDeleteParcel, isSplitShipmentAllowAddDelete } = props || {};

  const { t } = useTranslation();

  return (
    <div className="ocf-pi-bundle">
      {parcelLength > 1 && (
        <div className="ocf-pib-header">
          <div className="ocf-pib-title">
            {t("title.parcelWithCount", { count: parentIndex + 1 })}
          </div>
          {isSplitShipmentAllowAddDelete() && (
            <div className="ocf-pib-btn-container">
              <span onClick={() => onDeleteParcel(parentIndex)}>
                <DeleteOutlined
                  style={{ fontSize: 16, color: "hsl(0, 100%, 30%)", cursor: "pointer" }}
                />
              </span>
            </div>
          )}
        </div>
      )}
      <BaseInformation {...props} />
      <GoodsProperties {...props} />
    </div>
  );
};

export default ParcelInfoBundle;
